import { CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { MatBadgeModule } from '@angular/material/badge';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import {
    MatLegacyPaginator as MatPaginator,
    MatLegacyPaginatorIntl as MatPaginatorIntl,
    MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { ActivatedRoute, ResolveData, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { isArray } from 'chart.js/helpers';
import { TableVirtualScrollDataSource, TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, shareReplay, skip, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { AddReportButtonComponent } from '../../../../add-report-button/add-report-button.component';
import { CustomerEntity, CustomerStatusEnum, CustomerStatusTypeNames } from '../../../../dave-data-module/entities/customer.entity';
import { OfficeEntity } from '../../../../dave-data-module/entities/office.entity';
import { PartnerTypeEnum } from '../../../../dave-data-module/entities/partner.entity';
import { PersonEntity } from '../../../../dave-data-module/entities/person.entity';
import { Person2EntityEntityTypeEnum } from '../../../../dave-data-module/entities/person2entity.entity';
import { CustomerTypeResolver } from '../../../../dave-data-module/guards/customer-type.resolver';
import { CustomerResolver } from '../../../../dave-data-module/guards/customer.resolver';
import { OfficeResolver } from '../../../../dave-data-module/guards/office.resolver';
import { PartnerOfficeResolver } from '../../../../dave-data-module/guards/partner-office.resolver';
import { PartnerResolver } from '../../../../dave-data-module/guards/partner.resolver';
import { PersonResolver } from '../../../../dave-data-module/guards/person.resolver';
import { Person2EntityResolver } from '../../../../dave-data-module/guards/person2Entity.resolver';
import { PersonTypeResolver } from '../../../../dave-data-module/guards/personType.resolver';
import { customerBackendFilter, customerBackendSort, PaginatedCustomerService } from '../../../../dave-data-module/services/paginated-customer.service';
import { getFetched$ } from '../../../../dave-data-module/helper/helper';
import { State } from '../../../../dave-data-module/State';
import { partnersFeatureKey } from '../../../../dave-data-module/State/reducers/patners.reducer';
import { customersFeatureKey } from '../../../../dave-data-module/State/reducers/customers.reducer';
import { officeFeatureKey } from '../../../../dave-data-module/State/reducers/office.reducer';
import { personsFeatureKey } from '../../../../dave-data-module/State/reducers/person.reducer';
import { person2EntityFeatureKey } from '../../../../dave-data-module/State/reducers/person2Entity.reducer';
import { getCommissionTypes } from '../../../../dave-data-module/State/selectors/commissionType.selectors';
import { getCustomerTypeDictionary } from '../../../../dave-data-module/State/selectors/customers.selectors';
import { getCustomerSpecificationTypes, getCustomerSpecificationTypesFetched } from '../../../../dave-data-module/State/selectors/customerSpecificationType.selectors';
import { getOffices } from '../../../../dave-data-module/State/selectors/offices.selectors';
import { getOwnPartnerOffices, getPartner } from '../../../../dave-data-module/State/selectors/partners.selectors';
import { getPersonDictionary, getPersonsFetched } from '../../../../dave-data-module/State/selectors/person.selectors';
import { getPerson2Entities, getPerson2EntitiesFetched } from '../../../../dave-data-module/State/selectors/person2entity.selectors';
import { getPersonTypesFetched } from '../../../../dave-data-module/State/selectors/personType.selectors';
import { DaveDoubleIconModule } from '../../../../dave-double-icon/dave-double-icon.module';
import { ListCardDialogComponent, ListCardDialogComponentDialogData } from '../../../../dave-list-card/components/list-card-dialog/list-card-dialog.component';
import { NewReportDialogService } from '../../../../dave-reports/components/new-report-dialog/new-report-dialog.service';
import { isNotNullOrUndefined, SearchQueriesDebounceTime, TableColumnConfig } from '../../../../helper/helper';
import {
    AllDocumentEditorPageMeta,
    AllEventsMeta,
    AllOrdersMeta,
    AllReportsMeta,
    AllVideodokumentationenMeta,
    BookingPageMeta,
    CommissionMeta,
    ContactBookMeta,
    DocumentEditorPageMeta,
    GeneratedDocumentsPageMeta,
    GlobalStatisticsMeta,
    HistoryMeta,
    OrdersPageMeta,
    ReportsPageMeta,
    StatisticsPageMeta,
    VideodokumentationNewlMeta,
    VideodokumentationPageMeta,
} from '../../../../helper/page-metadata';
import { NewCommissionDialogComponent, NewCommissionDialogComponentDialogData } from '../../../../new-commission-dialog/new-commission-dialog.component';
import { PersonListEntryComponent } from '../../../../person-list-entry/person-list-entry.component';
import { CustomLabelService } from '../../../../services/custom-label/custom-label.service';
import { DefaultFilterService, FilterApps, FilterTypes } from '../../../../services/default-filter.service';
import { LoadingService } from '../../../../services/loading.service';
import { AppButtonModule } from '../../../app-button-module/app-button.module';
import { AppFilterModule } from '../../../app-filter-module/app-filter.module';
import { FilterOption, FILTER_TYPE_BOOLEAN, FILTER_TYPE_SEARCH_MULTI_SELECT, IFilterTypeSearchMultiSelectValue } from '../../../app-filter-module/app-filter/app-filter.component';
import { FormControlTyped } from '../../../typings';
import { BreakpointObserverService } from '../../services/breakpoint-observer.service';
import { PermissionService } from '../../services/permission.service';
import { GermanPaginatorIntl } from '../../../../services/german-paginator-intl.service';
import { CustomerSortProperty, GetTimeFromTimestamp, Office, SortDirection } from '@dave/types';
import { CustomerDataService } from '../../../../dave-data-module/services/customer-data.service';

interface TableData {
    Status: CustomerStatusEnum;
    KdNr: string;
    Kunde: CustomerEntity;
    PLZ: string;
    Ort: string;
    Street: string;
    Branche: string;
    Zentrale: string;
    Office: OfficeEntity;
    partnerOfficeId: number | null;
}

interface CustomerListFilterValues {
    [FilterTypes.PartnerOfficeId]: IFilterTypeSearchMultiSelectValue<number>[];
    [FilterTypes.Customers]: boolean;
    [FilterTypes.Supplier]: boolean;
    [FilterTypes.CustomerSpecificationTypeId]: IFilterTypeSearchMultiSelectValue<number>[];
}

@Component({
    selector: 'app-customer-list',
    templateUrl: './customer-list.component.html',
    styleUrls: ['./customer-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    providers: [
        PaginatedCustomerService,
        { provide: MatPaginatorIntl, useClass: GermanPaginatorIntl },
    ],
    imports: [
        CommonModule,
        MatListModule,
        FontAwesomeModule,
        RouterLink,
        RouterLinkActive,
        MatSortModule,
        MatTooltipModule,
        MatButtonModule,
        AppFilterModule,
        MatInputModule,
        AppButtonModule,
        MatBadgeModule,
        DaveDoubleIconModule,
        AddReportButtonComponent,
        RouterLink,
        ReactiveFormsModule,
        PersonListEntryComponent,

        MatTableModule,
        ScrollingModule,
        TableVirtualScrollModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
    ],
})
export class CustomerListComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    public static readonly RequiredResolvers: ResolveData = {
        [personsFeatureKey]: PersonResolver,
       // [customersFeatureKey]: CustomerResolver,
        partnerOffice: PartnerOfficeResolver,
       // [officeFeatureKey]: OfficeResolver,
        customerType: CustomerTypeResolver,
        [partnersFeatureKey]: PartnerResolver,
        [person2EntityFeatureKey]: Person2EntityResolver,
    };
    @ViewChild('wrapperElement') WrapperElement: ElementRef;
    @ViewChild('personCell') personCell: TemplateRef<any>;
    @ViewChild('scrollViewportForList') private scrollViewportForList?: CdkVirtualScrollViewport;
    @ViewChild(MatPaginator) private paginator?: MatPaginator;

    public FilterValues$: BehaviorSubject<CustomerListFilterValues> = new BehaviorSubject({
        [FilterTypes.PartnerOfficeId]: null,
        [FilterTypes.Customers]: true,
        [FilterTypes.Supplier]: false,
        [FilterTypes.CustomerSpecificationTypeId]: null,
    });
    Mobile$: Observable<boolean> = this.breakpointObserverService.MobileQuery.pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    @Input() ShowPersonButton = false;
    @Input() AdditionalButton: { label: string; path: any[] | string | null | undefined };
    @Input() ListStyle:
        | 'history'
        | 'kunden'
        | typeof ReportsPageMeta.Path
        | typeof GeneratedDocumentsPageMeta.Path
        | typeof StatisticsPageMeta.Path
        | typeof CommissionMeta.Path
        | typeof DocumentEditorPageMeta.Path
        | typeof OrdersPageMeta.Path
        | typeof VideodokumentationPageMeta.Path
        | typeof BookingPageMeta.Path = 'history';
    public SelectedId: number = null;
    public SearchForm: FormControlTyped<string> = new FormControl<string>('');
    public SelectedRowIndex: number;
    public CustomerStatusEnum = CustomerStatusEnum;
    public CustomerStatusTypeNames = CustomerStatusTypeNames;
    public ReportsPageMeta = ReportsPageMeta;

    /** Ob die Komponente unter einer bestimmten Breite ist */
    public IsSmallWidth$ = new BehaviorSubject(false);

    public ShowSearchBar$ = new BehaviorSubject(false);

    /** Konfiguration der Spalten der MatTable */
    public Columns: TableColumnConfig<TableData>[] = [
        { header: 'Kunde', name: 'Kunde' },
        { header: 'PLZ', name: 'PLZ' },
        { header: 'Ort', name: 'Ort' },
        { header: 'Straße', name: 'Street' },
        { header: 'Branche', name: 'Branche' },
        { header: 'Zentrale', name: 'Zentrale' },
    ];
    public IsSortableColumn(columnName: string): boolean {
        return !['Street', 'Branche', 'Zentrale'].includes(columnName);
    }
   // DefaultSort: keyof TableData = 'Kunde'; Default UpdatedAt DESC => kommt schon so vom BE

    /** Angezeigte Spalten der MatTable */
    public DisplayedColumns$: Observable<string[]>;

    /** Daten der Tabelle (jeweils nur die aktuell geladene Seite) */
    public DataSource$: Observable<TableVirtualScrollDataSource<TableData>>;
    /** Der `MatSort` der Tabelle - sortiert (bis das Backend Sortierung unterstützt) nur innerhalb der aktuell geladenen Seite */
    @ViewChild(MatSort) private matSort?: MatSort;

    /** Das Suchfeld-Element */
    @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

    /** Gibt ein Mal einen Wert aus, wenn der ngAfterViewInit Hook ausgelöst wird */
    private afterViewInit$ = new BehaviorSubject<boolean>(false);

    /** Gibt ein Mal einen Wert aus, wenn die Komponente zerstört wird */
    private onDestroy$ = new Subject<void>();
    private readonly _customerDataService = inject(CustomerDataService);

    protected headerTitle$: Observable<string> = this.cls.getSingle$('CustomerList');
    protected customerLabel$: Observable<string> = this.cls.getSingle$('CustomerSupplier');
    protected customersLabel$: Observable<string> = this.cls.getMultiple$('CustomerSupplier');
    protected customersRoleLabel$: Observable<string> = this.cls.getMultiple$('Customer');
    protected suppliersLabel$: Observable<string> = this.cls.getMultiple$('Supplier');

    // Statistik-Daten und Auftrags-Daten (commission) für das HTML-Template
    StatsPagePath = StatisticsPageMeta.Path;
    GlobalStatsPath = GlobalStatisticsMeta.Path;
    CommissionPath = CommissionMeta.Path;
    AllVideodokumentationenPath = AllVideodokumentationenMeta.Path;
    NewVideoMeta = VideodokumentationNewlMeta;
    VideoMeta = VideodokumentationPageMeta;
    HistoryMeta = HistoryMeta;
    AllDocsPath = AllDocumentEditorPageMeta.Path;
    DocsPath = DocumentEditorPageMeta.Path;
    VideodokumentationPath = VideodokumentationPageMeta.Path;
    AllEventsPath = AllEventsMeta.Path;
    ReportsMeta = ReportsPageMeta;
    BookingMeta = BookingPageMeta;
    AllReportsMeta = AllReportsMeta;
    OrdersPageMeta = OrdersPageMeta;
    AllOrdersMeta = AllOrdersMeta;

    public FilterSettings$: Observable<FilterOption[]> = this._customerDataService.defaultFilterSettings$;
    public FilterAmount$ = this.FilterValues$.pipe(
        map((val) => {
            let filterAmount = 0;
            if ((val[FilterTypes.Customers] || val[FilterTypes.Supplier]) && !(val[FilterTypes.Customers] && val[FilterTypes.Supplier])) {
                filterAmount++;
            }
            if (val[FilterTypes.PartnerOfficeId]?.length) {
                filterAmount++;
            }
            if (val[FilterTypes.CustomerSpecificationTypeId]?.length) {
                filterAmount++;
            }
            return filterAmount;
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    public PlaceHolder = new TableVirtualScrollDataSource<TableData>([]);

    /**
     * Als Feld mit `inject()` statt Konstruktor-Parameter, damit `_customerService` schon für die
     * darauf folgenden Feld-Initializer (`PageIsLoading$`, `length$`, `pageSize$`) sicher gesetzt
     * ist - analog zu `_materialService` in MaterialListViewComponent. Bei Injection über einen
     * Konstruktor-Parameter wäre die Zuweisungsreihenfolge zwischen Parameter-Property und
     * Feld-Initializern nicht garantiert.
     */
    private readonly _customerService = inject(PaginatedCustomerService);

    /** aus PaginatedCustomerService - analog zu PaginatedMaterialService in der Materialliste */
    public PageIsLoading$ = this._customerService.selectIsLoading$;
    public length$ = this._customerService.selectLength$;
    public pageSize$ = this._customerService.selectPageSize$;
    sortChange$ = new Subject<Sort>();
    private getBackendSort(active: string, direction: string): customerBackendSort {
        let property = CustomerSortProperty.UpdatedAt;

        switch(active) {
            case 'KdNr':
                property = CustomerSortProperty.CustomerNumber;
                break;
            case 'Kunde':
                property = CustomerSortProperty.CustomerName;
                break;
            case 'Ort':
                property = CustomerSortProperty.Location;
                break;
            case 'PLZ':
                property = CustomerSortProperty.PostalCode;
                break;
        }

        return {
            property,
            direction: direction === 'asc'
                ? SortDirection.asc
                : SortDirection.desc
        };
    }
    constructor(
        private breakpointObserverService: BreakpointObserverService,
        private newReportDialogService: NewReportDialogService,
        private route: ActivatedRoute,
        public Router: Router,
        private store: Store<State>,
        public PS: PermissionService,
        public ActivatedRoute: ActivatedRoute,
        public LS: LoadingService,
        private defaultFilterService: DefaultFilterService,
        private dialog: MatDialog,
        private personToEntityRes: Person2EntityResolver,
        private personRes: PersonResolver,
        private personTypesRes: PersonTypeResolver,
        protected cls: CustomLabelService,
    ) {
        this.FilterValues$.pipe(takeUntil(this.onDestroy$), skip(2)).subscribe((val) => {
            if (Object.keys(val).length !== 0) {
                this.defaultFilterService.SetFilterByApp(FilterApps.CustomerList, val);
            }
        });
        this.DisplayedColumns$ = this.IsSmallWidth$.pipe(
            map((isTableSmallWidth) => (isTableSmallWidth ? ['Status', 'KdNr', 'Kunde'] : ['Status', 'KdNr', 'Kunde', 'Street', 'PLZ', 'Ort', 'Branche', 'Zentrale', this.ShowPersonButton ? 'person' : null].filter(isNotNullOrUndefined))),
            shareReplay({ refCount: true, bufferSize: 1 }), // wird mehrmals subscribed
        );

        this.DataSource$ = combineLatest([this.store.select(getCustomerTypeDictionary), this.afterViewInit$.pipe(filter((v) => v))]).pipe(
            tap(() => {
                if (this.paginator) {
                    this.paginator.pageIndex = 0;
                }
            }),
            switchMap(([customerTypes]) =>
                merge(
                    this.sortChange$.pipe(
                        tap(() => (this.paginator.pageIndex = 0))
                    ),
                    this.paginator.page,
                    this.FilterValues$.pipe(
                        tap(() => (this.paginator.pageIndex = 0))
                    ),
                    this.SearchForm.valueChanges.pipe(
                        debounceTime(SearchQueriesDebounceTime),
                        tap(() => (this.paginator.pageIndex = 0))
                    ),
                ).pipe(
                    startWith(null),
                    switchMap(() => {
                        const page = this.paginator;
                        const offset = page.pageIndex * page.pageSize;
                        const search = this.SearchForm.value;
                        const filterVal = this.FilterValues$.value;
                        const sort = this.getBackendSort(this.matSort.active, this.matSort.direction);
                        this._customerService.loadPage({
                            offset,
                            pageSize: page.pageSize,
                            searchQuery: search,
                            filterObject: this.toBackendFilter(filterVal),
                            sortProperty: {
                                property: sort.property,
                                direction: sort.direction.toString() === "asc"
                                    ? SortDirection.asc
                                    : SortDirection.desc
                            }
                        });
                        return this._customerService.selectItems$;
                    }),
                    map((customers) => new TableVirtualScrollDataSource<TableData>(customers.map((c) => this.toTableData(c, customerTypes)))),
                ),
            ),
            tap((dataSource) => {
                // this.matSort sollte hier immer gesetzt sein (da ngAfterViewInit gefeuert haben muss),
                // aber das weiß TypeScript natürlich nicht.
               // this.matSort && (dataSource.sort = this.matSort);

                // Der Standard-`sortingDataAccessor` kommt mit numerischen Strings - wie der
                // Kundennummer - nicht klar und sortiert nicht. Workaround:
                // TODO(Backend): sortiert aktuell nur innerhalb der jeweils geladenen Seite,
                // solange das Backend keine Sortierung unterstützt.
                dataSource.sortingDataAccessor = (object, key) => {
                    switch (key) {
                        case 'Kunde': // ausnahme für kunden name
                            return `${object['Kunde']['Name']}`.trim().toLowerCase();
                        case 'KdNr':
                            return `${object[key]}`.trim().toLowerCase();
                        default:
                            return object[key];
                    }
                };
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        // Seiteneffekt: wenn das Suchfeld eingeblendet wird, fokussieren - asynchron, damit Angular
        // das Suchfeld erst anzeigt und danach fokussiert. Wenn ausgeblendet, den Suchwert löschen.
        this.ShowSearchBar$.pipe(takeUntil(this.onDestroy$)).subscribe((isSearching) => (isSearching ? setTimeout(() => this.inputRef && this.inputRef.nativeElement.focus()) : this.SearchForm.setValue('')));
    }

    private toBackendFilter(
        filterVal: CustomerListFilterValues
    ): customerBackendFilter {

        return {

            partnerOfficeIds:
                filterVal[FilterTypes.PartnerOfficeId]
                    ?.map(v => Number(v.id)),


            customerSpecificationTypeIds:
                filterVal[FilterTypes.CustomerSpecificationTypeId]
                    ?.map(v => Number(v.id)),


            onlyCustomers:
                filterVal[FilterTypes.Customers],


            onlySuppliers:
                filterVal[FilterTypes.Supplier],
        };
    }

    private buildRouterLink(customerId: number): any[] {
        switch (this.ListStyle) {
            case 'history':
                return ['/' + HistoryMeta.Path, customerId];
            case 'kunden':
                return ['/kunden', customerId];
            case StatisticsPageMeta.Path:
                return ['/' + StatisticsPageMeta.Path, customerId];
            case CommissionMeta.Path:
                return ['/' + CommissionMeta.Path, customerId];
            case DocumentEditorPageMeta.Path:
                return ['/' + DocumentEditorPageMeta.Path, customerId];
            case ReportsPageMeta.Path:
                return ['/' + ReportsPageMeta.Path, customerId];
            case GeneratedDocumentsPageMeta.Path:
                return ['/' + GeneratedDocumentsPageMeta.Path, customerId];
            case VideodokumentationPageMeta.Path:
                return ['/' + VideodokumentationPageMeta.Path, customerId];
            case OrdersPageMeta.Path:
                return ['/' + OrdersPageMeta.Path, customerId];
            case BookingPageMeta.Path:
                return ['/' + BookingPageMeta.Path, customerId];
            default:
                return ['/kunden', customerId];
        }
    }
    public sortChanged(sort: Sort) {

        this.sortChange$.next(sort);
        // let property = CustomerSortProperty.UpdatedAt;
        //
        // switch(sort.active)
        // {
        //     case 'KdNr':
        //         property = CustomerSortProperty.CustomerNumber;
        //         break;
        //
        //     case 'Kunde':
        //         property = CustomerSortProperty.CustomerName;
        //         break;
        //
        //     case 'Ort':
        //         property = CustomerSortProperty.Location;
        //         break;
        //
        //     case 'PLZ':
        //         property = CustomerSortProperty.PostalCode;
        //         break;
        // }
        //
        // const customerSort: customerBackendSort = {
        //     property: property,
        //     direction:
        //         sort.direction === 'asc'
        //             ? SortDirection.asc
        //             : SortDirection.desc
        // };
        //
        // this._customerService.setSort({
        //     sortProperty: customerSort,
        //     sortDirection:  sort.direction === 'asc'
        //         ? SortDirection.asc
        //         : SortDirection.desc
        // });


    }

    private officeToOfficeEntiy(office: Office): OfficeEntity {
        if (!office) {
            return null;
        }

        return new OfficeEntity(
            Number(office.Id),
            office.PhoneNumber ?? null,
            office.Email ?? null,
            office.InvoiceEmail ?? null,
            office.Street ?? null,
            office.PostalCode ?? null,
            office.City ?? null,
            office.Country ?? null,
            office.Longitude?.toString() ?? null,
            office.Latitude?.toString() ?? null,
            office.IsHeadquarter ?? null,
            Number(office.CustomerId),
            office.FaxNumber ?? null,
            office.MobileNumber ?? null,
            office.IBAN ?? null,
            office.Swift ?? null,
            office.BankName ?? null,
            office.BankLocation ?? null,
            office.BankPostalCode ?? null,
            office.BankSortCode ?? null,
            office.UpdatedAt ? GetTimeFromTimestamp(+office.UpdatedAt) : null,
        );
    }

    private toTableData(customer: CustomerEntity, customerTypes: Dictionary<{ Id: number; Name: string }>): TableData {
        const office = customer?.Offices?.[0];
        const ctypes = customer.CustomerTypeIds.map((id) => customerTypes[id])
            .filter(isNotNullOrUndefined)
            .map((ct) => ct.Name)
            .join(' ');


        return {
            Status: customer.CustomerStatus,
            KdNr: customer.CustomerNo || '--',
            Kunde: customer,
            PLZ: office?.PostalCode || '',
            Ort: office?.City || '',
            Street: office?.Street || '',
            Branche: ctypes,
            Zentrale: office?.PhoneNumber || '',
            RouterLink: this.buildRouterLink(customer.Id),
            Office: this.officeToOfficeEntiy(office),
            partnerOfficeId: customer.PartnerOfficeId,
        } as TableData;
    }

    ngAfterViewInit() {
        this.afterViewInit$.next(true);
        /* D277-4627 - Fix beim scrollen nach oben, das Elemente zurück springen lässt */
        if (this.scrollViewportForList) {
            const scrollable = this.scrollViewportForList?.elementRef.nativeElement;
            if (scrollable && scrollable.children) {
                const spacer = scrollable.children[1];
                if (spacer && spacer.classList.contains('cdk-virtual-scroll-spacer')) {
                    scrollable.insertBefore(spacer, scrollable.firstChild);
                } else {
                    console.error('Spacer nicht gefunden');
                }
            } else {
                console.error('Scrollable nicht gefunden');
            }
        }
    }

    ngOnInit() {
        if (this.Router.url.indexOf('history') > 0) {
            const idParts: string[] = this.Router.url.substr(this.Router.url.indexOf('history')).split('/');
            if (!!idParts && idParts.length > 0) {
                this.SelectedId = +idParts[0];
            }
        }
        if (this.Router.url.indexOf('kunden') > 0) {
            this.ListStyle = 'kunden';
        } else if (this.Router.url.includes(StatisticsPageMeta.Path)) {
            this.ListStyle = StatisticsPageMeta.Path;
        } else if (this.Router.url.includes(CommissionMeta.Path)) {
            this.ListStyle = CommissionMeta.Path;
        } else if (this.Router.url.includes(DocumentEditorPageMeta.Path)) {
            this.ListStyle = DocumentEditorPageMeta.Path;
        } else if (this.Router.url.includes(VideodokumentationPageMeta.Path)) {
            this.ListStyle = VideodokumentationPageMeta.Path;
        } else if (this.Router.url.includes(ReportsPageMeta.Path)) {
            this.ListStyle = ReportsPageMeta.Path;
        }
        combineLatest([this.FilterSettings$, this.defaultFilterService.GetFilterByApp$(FilterApps.CustomerList)])
            .pipe(take(1), takeUntil(this.onDestroy$))
            .subscribe(([val, filterValues]) => {
                this.FilterValues$.next({
                    [FilterTypes.PartnerOfficeId]: isArray(filterValues[FilterTypes.PartnerOfficeId]) ? (filterValues[FilterTypes.PartnerOfficeId] as IFilterTypeSearchMultiSelectValue<number>[]) : [],
                    [FilterTypes.CustomerSpecificationTypeId]: isArray(filterValues[FilterTypes.CustomerSpecificationTypeId]) ? (filterValues[FilterTypes.CustomerSpecificationTypeId] as IFilterTypeSearchMultiSelectValue<number>[]) : [],
                    [FilterTypes.Customers]: isNotNullOrUndefined(filterValues[FilterTypes.Customers]) ? filterValues[FilterTypes.Customers] : true,
                    [FilterTypes.Supplier]: isNotNullOrUndefined(filterValues[FilterTypes.Supplier]) ? filterValues[FilterTypes.Supplier] : false,
                });
            });
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();

        this.ShowSearchBar$.complete();
        this.FilterValues$.complete();
        this.afterViewInit$.complete();
        this.IsSmallWidth$.complete();
    }

    ngAfterViewChecked() {
        this.sizing();
    }

    @HostListener('window:resize')
    sizing() {
        const smallWidth = this.WrapperElement?.nativeElement.offsetWidth < 640;

        if (this.IsSmallWidth$.value !== smallWidth) {
            this.IsSmallWidth$.next(smallWidth);
        }
    }

    NewInvoice() {
        this.newReportDialogService.OpenNewReportDialog();
    }
    public FilterCustomColumns(c: any) {
        return !(c.name === 'Kunde');
    }
    public CreateCommission() {
        firstValueFrom(
            this.store.select(getPartner).pipe(switchMap((partner) => (partner.PartnerTypeId === PartnerTypeEnum.TSB ? this.store.select(getCommissionTypes).pipe(map((cts) => cts.find((ct) => ct.AdditionalData?.IsDefault))) : of(null)))),
        ).then((commissionType) => {
            this.dialog.open<NewCommissionDialogComponent, NewCommissionDialogComponentDialogData>(NewCommissionDialogComponent, {
                ...NewCommissionDialogComponent.DefaultConfig,
                data: {
                    CreateCommissionPayload: {
                        auftragseingangDurchAuftraggeber: new Date().toDateString(),
                    },
                    ForceCommissionTypeId: commissionType?.Id,
                },
            });
        });
    }

    public highlight(rowData) {
        this.SelectedRowIndex = rowData.Kunde.Id;
    }
    public OpenPersonPopup(customer: CustomerEntity) {
        const dialogRef = this.dialog.open<ListCardDialogComponent<PersonEntity>, ListCardDialogComponentDialogData<PersonEntity>>(ListCardDialogComponent, {
            ...ListCardDialogComponent.DefaultConfig,
            data: {
                GetRouterLinkFunction: (person) => ['/' + ContactBookMeta.Path, person.Id],
                Headline: customer.AnsprechpartnerText(),
                EmptyText: 'Keine ' + customer.AnsprechpartnerText() + ' vorhanden',
                Data$: combineLatest([
                    this.store.select(getPersonsFetched).pipe(
                        tap((fetched) => {
                            if (!fetched) {
                                this.personRes.resolve();
                            }
                        }),
                    ),
                    this.store.select(getPerson2EntitiesFetched).pipe(
                        tap((fetched) => {
                            if (!fetched) {
                                this.personToEntityRes.resolve();
                            }
                        }),
                    ),
                    this.store.select(getPersonTypesFetched).pipe(
                        tap((fetched) => {
                            if (!fetched) {
                                this.personTypesRes.resolve();
                            }
                        }),
                    ),
                ]).pipe(
                    filter((fetched) => fetched.every((f) => f)),
                    switchMap(() => this.store.select(getPerson2Entities)),
                    map((p2e) => p2e.filter((p) => p.EntityId === customer.Id && p.EntityType === Person2EntityEntityTypeEnum.Customer)),
                    switchMap((p2e) => this.store.select(getPersonDictionary).pipe(map((persons) => p2e.map((p) => persons[p.PersonId])))),
                    map((persons) => persons.filter((p) => p && !p.Deleted).sort((a, b) => a.UpdatedAt.getTime() - b.UpdatedAt.getTime())),
                ),
                DisplayedCollums: ['DisplayName'],
                Clickable: true,
                HasSuffixButton: false,
                Search: false,
                CustomCellTemplates: {
                    DisplayName: this.personCell,
                },
            },
        });
    }
}
